<script lang="ts">
	import type { PistolMapping } from 'endpoint/holster_line/configurator'
	import type { HolsterAccessoryWithColors } from 'endpoint/holster_line/holster_accessory'
	import type { HolsterAccessoryAndFriends } from 'endpoint/holster_accessory/holster_accessory'
	import type { StockItemPurchasabilityAndWaitlist } from 'endpoint/stock_item/can_accessory_be_purchased_in_the_configurator'

	import { get_accessory_image_file } from 'shared/bzns/holster/get_image_file'
	import { calculate_configured_price } from 'shared/bzns/holster/holster_pricing'
	import {
		holster_accessory_is_valid,
		select_holster_accessory,
		all_holster_accessories_attributes,
		holster_accessories_default_selected_attributes,
		holster_accessory_selected_attributes,
	} from 'shared/bzns/holster/accessories'
	import { map_object_values } from 'shared/object/map_object'
	import array_group from 'shared/array/array_group'
	import { storefront_api2_prefix } from 'shared/var'
	import fetchy from 'shared/fetchy'
	import get_valid_accessory_groups from 'shared/bzns/holster/get_valid_accessory_groups'
	import build_querystring from 'shared/build_querystring'
	import { serialize_to_address_bar, deserialize_from_address_bar } from 'shared/query_serialization'
	import fnum, { financial_min } from 'shared/number'
	import map_has from 'shared/map_has'
	import assert from 'shared/assert'

	import ConfiguratorWrapper from 'shared/component/ConfiguratorWrapper.svelte'
	import ConfiguratorImages from 'shared/component/ConfiguratorImages.svelte'
	import ColorPicker from 'shared/component/ColorPicker.svelte'
	import HolsterAccessoryInputs from './HolsterAccessoryInputs.svelte'
	import AccessorySelector from 'component/AccessorySelector.svelte'

	export let lead_time_text: string
	export let product_title: string
	export let holster_line: SingleJsonApiResponse<`holster_line`>
	export let accessories: HolsterAccessoryAndFriends[]
	export let colors: TrexColor[]
	export let promo_active: boolean
	export let product_ref: number
	export let pistol_mapping: PistolMapping | undefined
	export let add_on_accessories: HolsterAccessoryWithColors[]

	let pistol_ref: number | null = null
	let pistol_light_ref: number | null = null
	let threaded: boolean
	let holster_backer_ref: number | null = null
	let pistol_magazine_bucket_ref: number | null = null

	const initial_shopping_cart_values = typeof window !== `undefined`
		? deserialize_from_address_bar()
		: {}

	const price_can_increase = accessories.some(a => a.colors.some(c => c.additional_price))

	const holster_accessory_ref = accessories[0].holster_accessory_ref

	const attributes = all_holster_accessories_attributes(accessories)

	let holster_accessory = accessories.find(({ holster_accessory_variation_ref }) => holster_accessory_variation_ref === initial_shopping_cart_values.holster_accessory_variation_ref)

	let selected_attributes = holster_accessory
		? holster_accessory_selected_attributes(holster_accessory)
		: holster_accessories_default_selected_attributes(accessories)[holster_accessory_ref]

	holster_accessory = select_holster_accessory(accessories, selected_attributes, pistol_magazine_bucket_ref, holster_backer_ref)

	$: holster_accessory = select_holster_accessory(accessories, selected_attributes, pistol_magazine_bucket_ref, holster_backer_ref)

	$: base_price = holster_accessory?.base_price || financial_min(...(accessories.map(({ base_price }) => base_price)))

	const color_ref_to_holster_accessory_color = new Map(holster_accessory.colors.map(holster_accessory_color => [
		holster_accessory_color.color_ref,
		holster_accessory_color,
	]))

	const color_ref_to_additional_price = new Map(holster_accessory.colors.map(holster_accessory_color => [
		holster_accessory_color.color_ref,
		fnum(holster_accessory_color.additional_price.toString()),
	]))
	const get_color_additional_price = (color_ref: number) => {
		assert(map_has(color_ref_to_additional_price, color_ref))
		const additional_price = color_ref_to_additional_price.get(color_ref)
		return additional_price
	}

	const available_colors = colors.filter(({ color_ref }) => color_ref_to_holster_accessory_color.has(color_ref))

	const variation_ref_to_add_on_accessory = new Map(add_on_accessories.map(accessory => [
		accessory.holster_accessory_variation_ref,
		accessory,
	]))

	const is_valid_accessory_and_color_ref = (x: unknown): x is {
		holster_accessory_variation_ref: number
		color_ref: number
	} => !!x && typeof x === `object`
		&& `holster_accessory_variation_ref` in x && `color_ref` in x
		&& typeof x.holster_accessory_variation_ref === `number` && typeof x.color_ref === `number`
		&& map_has(variation_ref_to_add_on_accessory, x.holster_accessory_variation_ref)
		&& !!variation_ref_to_add_on_accessory.get(x.holster_accessory_variation_ref).holster_accessory_colors.find(({ color_ref }) => color_ref === x.color_ref)

	const initial_selected_accessories = [
		...(Array.isArray(initial_shopping_cart_values.hardwared_holster_accessories) ? initial_shopping_cart_values.hardwared_holster_accessories : []),
		...(Array.isArray(initial_shopping_cart_values.non_hardwared_holster_accessories) ? initial_shopping_cart_values.non_hardwared_holster_accessories : []),
	].filter(is_valid_accessory_and_color_ref)

	const default_color_ref = holster_accessory.colors.find(({ is_default }) => is_default)!.color_ref

	const color_ref_to_color = new Map(available_colors.map(color => [
		color.color_ref,
		color,
	]))
	const get_color = (color_ref: number) => {
		const color = color_ref_to_color.get(color_ref)
		assert(color)
		return color
	}

	let selected_color = typeof initial_shopping_cart_values.color_ref === `number`
		&& color_ref_to_color.get(initial_shopping_cart_values.color_ref)
		|| get_color(default_color_ref)

	let selected_add_on_accessory_refs = new Set([
		...add_on_accessories
			.filter(({ include_one_with_holster }) => include_one_with_holster)
			.map(({ holster_accessory_ref }) => holster_accessory_ref),
		...initial_selected_accessories
			.map(e => variation_ref_to_add_on_accessory.get(e.holster_accessory_variation_ref)!.holster_accessory_ref),
	])

	$: shopping_cart_object = {
		holster_accessory_variation_ref: holster_accessory?.holster_accessory_variation_ref,
		color_ref: selected_color.color_ref,
		pistol_ref,
		pistol_light_ref,
		threaded,
		hardwared_holster_accessories: hardwared_holster_accessories.map(accessory => ({
			holster_accessory_variation_ref: accessory.holster_accessory_variation_ref,
			color_ref: holster_accessory_ref_to_color_ref.get(accessory.holster_accessory_ref),
		})),
		non_hardwared_holster_accessories: non_hardwared_holster_accessories.map(accessory => ({
			holster_accessory_variation_ref: accessory.holster_accessory_variation_ref,
			color_ref: holster_accessory_ref_to_color_ref.get(accessory.holster_accessory_ref),
		})),
	}

	$: shopping_cart_object && typeof window !== `undefined` && serialize_to_address_bar(shopping_cart_object)

	$: configured_price = calculate_configured_price({
		base_price,
		promo_discount_rate: holster_accessory?.promo_discount_rate,
		promo_active,
		color_additional_price: get_color_additional_price(selected_color.color_ref),
		valid_accessories_for_selection,
		selected_accessory_refs: selected_add_on_accessory_refs,
		holster_accessory_ref_to_color_ref,
	})

	$: accessory_image_file_name = get_accessory_image_file({
		accessory_image_file_name: holster_accessory.image_file_name,
		color_image_file_name: selected_color.image_file_name_string,
		holster_line_name: holster_line.data.attributes.image_file_name_string.split(`/`)[0],
	})

	const belt_loop_color_image_file_name = `00016` // Black

	const cast_belt_width_value = (value: string | undefined) => value !== undefined
		? value === `1.75"` ? `1.75"` : `1.5"`
		: undefined

	$: belt_loop_image_file_name = holster_accessory.has_belt_loops && get_accessory_image_file({
		accessory_image_file_name: holster_accessory.image_file_name,
		color_image_file_name: belt_loop_color_image_file_name,
		holster_line_name: holster_line.data.attributes.image_file_name_string.split(`/`)[0],
		belt_loop_size: cast_belt_width_value(holster_accessory.attributes.find(attribute => attribute.name === `Belt Width`)?.value),
	})

	const select_default_accessory_color_if_there_is_only_one_color_option = (accessories: HolsterAccessoryWithColors[]) => new Map(accessories
		.filter(({ holster_accessory_colors }) => holster_accessory_colors.length === 1)
		.map(({ holster_accessory_ref, holster_accessory_colors }) => [
			holster_accessory_ref,
			holster_accessory_colors[0].color_ref,
		]))

	let holster_accessory_ref_to_color_ref = select_default_accessory_color_if_there_is_only_one_color_option(add_on_accessories)

	const holster_accessory_to_attributes = map_object_values(
		array_group(add_on_accessories, ({ holster_accessory_ref }) => holster_accessory_ref),
		holster_accessories => all_holster_accessories_attributes(holster_accessories)
	)

	let holster_accessory_to_selected_attributes = holster_accessories_default_selected_attributes(add_on_accessories)

	let accessories_with_purchasability = add_on_accessories.map(accessory => ({
		...accessory,
		purchasable: true,
	}))

	const ops_api_fetch = fetchy(storefront_api2_prefix)

	async function update_accessories_with_purchasability(accessories: HolsterAccessoryWithColors[]) {
		const stock_item_refs = accessories.map(({ stock_item_ref }) => stock_item_ref).filter(Boolean)

		const stock_item_ref_to_purchasability = stock_item_refs.length
			? Object.fromEntries(
				((await ops_api_fetch(`/stock_item/can_accessory_be_purchased_in_the_configurator${ build_querystring({ stock_item_refs }) }`)) as StockItemPurchasabilityAndWaitlist[])
					.map(({ stock_item_ref, ...rest }) => [ stock_item_ref, rest ])
			)
			: {}

		accessories_with_purchasability = accessories.map(accessory => {
			const purchasability = accessory.can_create_on_demand
				? { purchasable: true }
				: accessory.stock_item_ref && stock_item_ref_to_purchasability[accessory.stock_item_ref]

			assert(purchasability)

			return {
				...accessory,
				...purchasability,
			}
		})
	}

	update_accessories_with_purchasability(add_on_accessories)

	$: valid_accessories_for_selection = accessories_with_purchasability
		.filter(accessory => holster_accessory_is_valid(
			accessory,
			holster_accessory_to_selected_attributes[accessory.holster_accessory_ref]
		))
		.sort((a, b) => a.sort_order - b.sort_order)

	const get_hardwared_holster_accessories = <ACCESSORY extends HolsterAccessoryWithColors>({
		valid_accessories_for_selection,
		selected_add_on_accessory_refs,
	}: {
		valid_accessories_for_selection: ACCESSORY[]
		selected_add_on_accessory_refs: Set<number>
	}) => {
		if (!holster_line.data.attributes.configurator_allow_multiple_attachments) {
			const attachment = valid_accessories_for_selection.find(accessory => accessory.is_attached_to_holster
				&& selected_add_on_accessory_refs.has(accessory.holster_accessory_ref))

			return attachment ? [ attachment ] : []
		}

		const attachments: ACCESSORY[] = []
		const invalid_attachment_refs = []

		valid_accessories_for_selection.filter(({ holster_accessory_ref }) => selected_add_on_accessory_refs.has(holster_accessory_ref))
			.forEach(accessory => {
				if (accessory.is_attached_to_holster) {
					accessory.hardware_parent_product_refs.length
						? accessory.hardware_parent_product_refs.includes(attachments.at(-1)?.product_ref || product_ref)
							? attachments.push(accessory)
							: invalid_attachment_refs.push(accessory.holster_accessory_ref)
						: attachments.push(accessory)
				}
			})

		while (attachments.length && !attachments.at(-1)!.can_be_last_attachment) {
			const removed = attachments.pop()
			invalid_attachment_refs.push(removed!.holster_accessory_ref)
		}

		invalid_attachment_refs.forEach(ref => selected_add_on_accessory_refs.delete(ref))
		selected_add_on_accessory_refs = selected_add_on_accessory_refs

		return attachments
	}

	$: hardwared_holster_accessories = get_hardwared_holster_accessories({
		valid_accessories_for_selection,
		selected_add_on_accessory_refs,
	})

	$: hardwared_holster_accessory_variation_refs = new Set(hardwared_holster_accessories.map(({ holster_accessory_variation_ref }) => holster_accessory_variation_ref))

	$: non_hardwared_holster_accessories = valid_accessories_for_selection // Start with valid_accessories_for_selection to apply order
		.filter(({ holster_accessory_ref, holster_accessory_variation_ref }) => selected_add_on_accessory_refs.has(holster_accessory_ref)
			&& !hardwared_holster_accessory_variation_refs.has(holster_accessory_variation_ref))

	$: valid_accessory_groups = get_valid_accessory_groups({
		valid_accessories_for_selection,
		hardwared_holster_accessories,
		root_product_ref: product_ref,
		selected_accessory_refs: selected_add_on_accessory_refs,
	})
</script>

<ConfiguratorWrapper
	base_price={holster_accessory.base_price}
	promo_discount_rate={holster_accessory.promo_discount_rate}
	{promo_active}
	{configured_price}
	{product_title}
	cart_endpoint={`/item/add_holster_accessory`}
	{shopping_cart_object}
	show_color_picker={available_colors.length > 1}
	compact_color_picker={!valid_accessory_groups.length}
	{product_ref}
	{price_can_increase}
>

	<ConfiguratorImages
		slot=image
		holster_line={holster_line.data.attributes}
		product_image_alt={holster_accessory.name}
		product_image_file_name={accessory_image_file_name}
		attachment_image_alt="Ironside Belt Loop Pair"
		attachment_image_file_name={belt_loop_image_file_name || null}
		flip_x={holster_accessory.preview_flip_x}
		bottom_overlay_text={selected_color?.name}
		promo_discount_rate={holster_accessory.promo_discount_rate}
		{lead_time_text}
		{promo_active}
		is_accessory={true}
	/>

	<ColorPicker
		slot=color_picker
		colors={available_colors}
		promo_discount_rate={holster_accessory.promo_discount_rate}
		{promo_active}
		{color_ref_to_additional_price}
		bind:selected={selected_color}
	/>

	<div slot=input>
		<HolsterAccessoryInputs
			bind:selected_attributes
			{accessories}
			{holster_accessory}
			{attributes}
			{pistol_mapping}
			bind:pistol_ref
			bind:pistol_light_ref
			bind:threaded
			bind:holster_backer_ref
			bind:pistol_magazine_bucket_ref
			{holster_line}
			{initial_shopping_cart_values}
		/>

		{#if valid_accessory_groups.length}
			<AccessorySelector
				holster_line_image_file_name_string={holster_line.data.attributes.image_file_name_string}
				holster_line_image_position_algorithm={holster_line.data.attributes.image_position_algorithm}
				accessory_groups={valid_accessory_groups}
				{accessories_with_purchasability}
				{holster_accessory_to_attributes}
				bind:holster_accessory_to_selected_attributes
				bind:selected_accessory_refs={selected_add_on_accessory_refs}
				bind:holster_accessory_ref_to_color_ref
				{promo_active}
			/>
		{/if}
	</div>

</ConfiguratorWrapper>
